import * as React from "react";
import { createPortal } from "react-dom";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  titleId: string;
  children: React.ReactNode;
};

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean>(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false
  );
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

export function Drawer({ open, onClose, titleId, children }: DrawerProps) {
  const isMobile = useIsMobile();
  const panelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab") {
        const focusables = getFocusable(panelRef.current);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first) {
            (last as HTMLElement).focus();
            e.preventDefault();
          }
        } else {
          if (active === last) {
            (first as HTMLElement).focus();
            e.preventDefault();
          }
        }
      }
    }
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000]"
      aria-labelledby={titleId}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/40 a11y-reduce-motion:transition-none transition-opacity duration-200"
        onClick={onClose}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={[
          "absolute bg-[var(--bg-color)] text-[var(--text-color)]",
          "outline-none",
          "a11y-reduce-motion:transition-none transition-transform duration-300",
          isMobile
            ? "inset-0 translate-y-0"
            : "top-0 right-0 h-full w-[420px] max-w-[90vw] translate-x-0 shadow-[0_10px_40px_rgba(2,6,23,0.25)] border-l border-[var(--border-color)]",
        ].join(" ")}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

function getFocusable(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  const selectors = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    '[tabindex]:not([tabindex="-1"])',
  ].join(",");
  return Array.from(root.querySelectorAll<HTMLElement>(selectors)).filter(
    (el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden")
  );
}
